/**
 * parasutMapping.js — Paraşüt (muhasebe SaaS) admin eşleme ekranı için
 * React Query hook'ları.
 *
 * Backend: backend/routes/parasut_admin.py
 * Uçlar (hepsi /admin/parasut/ altında, hepsi require_admin):
 *   config · products/remote · accounts/remote ·
 *   course-mappings[/:id] · bank-mappings[/:id] ·
 *   outbox/summary · outbox/blocked
 *
 * Bilinçli kapsam dışı (bu turda UI yok, uçlar backend'de var):
 *   contact-mappings/{user_id}, students/{user_id}/sync-contact —
 *   öğrenci↔cari eşleme ekranı bu turun kapsamında DEĞİL.
 *
 * Bayrak (PARASUT_ENABLED) kapalıyken course/bank mapping CRUD'u ve outbox
 * uçları normal çalışır (yalnızca kendi DB'mizi okur/yazar) — YALNIZCA
 * products/remote ve accounts/remote 503 `parasut_disabled` döner (bkz.
 * routes/parasut_admin.py modül docstring'i). Bu yüzden bu iki hook'un
 * `enabled` parametresi VAR (varsayılan false) — sayfa açılışında otomatik
 * çağrılmaz, admin "Paraşüt'ten seç" tıklayınca tetiklenir.
 *
 * FAZ 4 eklentileri (2026-09-29, Tur 2/feature/parasut-fatura-faz4-web) —
 * backend'de HAZIR (routes/parasut_admin.py "FAZ 4" bölümü, salt okundu):
 *   outbox (genel liste) · outbox/{id}/retry · outbox/{id}/resolve-needs-
 *   verification · outbox/{id}/resolve-unknown · payments/invoice-status
 *   (toplu) · payments/{id}/invoice-status (tekil, BU DOSYADA KULLANILMIYOR —
 *   ledger ekranı satır başına istek atmaz, bkz. usePaymentsInvoiceStatusBulk
 *   docstring'i) · payments/{id}/mark-refunded.
 *
 * Tur 3 (aynı gün, backend-teyit turu) — Tur 2'de raporlanan "outbox_id
 * yanıtta yok" sözleşme boşluğu KAPANDI: `_invoice_status_summary()`
 * (parasut_admin.py, salt okundu) artık `outbox_id` döndürüyor. `usePayments
 * InvoiceStatusBulk` sonucundaki her kayıtta `outbox_id` mevcuttur — ledger
 * ekranı (`parasutStatusCell.jsx`) `failed` satırlarda artık BURADAN
 * doğrudan `useRetryOutboxItem({outboxId: statusInfo.outbox_id, note})`
 * çağırabilir; genel Outbox sekmesine link YEDEK olarak kalır.
 *
 * Tur 3 — GET/PUT /admin/parasut/invoice-item-mapping ARTIK HAZIR (backend'de
 * doğrulandı, salt okundu): eşleme yoksa GET 200 + JSON gövde `null` döner
 * (bir sarmalayıcı DEĞİL — `{"mapping": null}` değil, doğrudan `null`).
 * `useInvoiceItemMapping`/`useSetInvoiceItemMapping` bu sözleşmeye göre
 * yazıldı ve DOĞRULANDI.
 *
 * Tur 5 (staging bulgusu) — Paraşüt'ün KENDİ `page[size]` sınırı 25'tir;
 * `useParasutRemoteProducts`/`useParasutRemoteAccounts`'un ÖNCEKİ `pageSize=100`
 * varsayılanı staging'de hata veriyordu (backend route'unun kendi `Query(25,
 * ge=1, le=100)` sınırı bunu ENGELLEMİYOR — `le=100` bizim API'mize karşı,
 * Paraşüt'e giden `page[size]` değerini SINIRLAMIYOR, salt okundu:
 * `routes/parasut_admin.py`, `services/parasut/client.py::list_products/
 * list_accounts`). Varsayılan `pageSize` 25'e düşürüldü. Yanıt şekli SALT
 * `{"data": [...]}` — sayfalama METADATA'sı (toplam sayfa/sonraki link) YOK;
 * `AdminParasutMapping.jsx` `RemotePicker` bu yüzden "daha fazla var mı"
 * kararını dönen kayıt sayısının `pageSize`'a EŞİT olup olmadığına bakarak
 * (sezgisel, kesin değil) verir — bkz. o bileşenin docstring'i.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

// ─── Query key factory ────────────────────────────────────────────────────────

export const parasutMappingKeys = {
  all: ['parasutMapping'],
  config: ['parasutMapping', 'config'],
  remoteProducts: (pageSize, pageNumber) => ['parasutMapping', 'remoteProducts', pageSize, pageNumber],
  remoteAccounts: (pageSize, pageNumber) => ['parasutMapping', 'remoteAccounts', pageSize, pageNumber],
  courseMappings: (onlyUnmapped, limit, skip) => ['parasutMapping', 'courses', onlyUnmapped, limit, skip],
  bankMappings: ['parasutMapping', 'banks'],
  outboxSummary: ['parasutMapping', 'outbox', 'summary'],
  outboxBlocked: (blockReason, limit, skip) => ['parasutMapping', 'outbox', 'blocked', blockReason, limit, skip],
  outboxList: (status, entityType, limit, skip) => ['parasutMapping', 'outbox', 'list', status, entityType, limit, skip],
  paymentsInvoiceStatus: (ids) => ['parasutMapping', 'paymentsInvoiceStatus', ids.slice().sort().join(',')],
  invoiceItemMapping: ['parasutMapping', 'invoiceItemMapping'],
};

// ─── Config ───────────────────────────────────────────────────────────────────

/** Bayrak/host özeti — secret/token değeri asla dönmez (bkz. backend docstring). */
export function useParasutConfig() {
  return useQuery({
    queryKey: parasutMappingKeys.config,
    queryFn: async () => {
      const res = await apiClient.get('/admin/parasut/config');
      return res.data;
    },
    staleTime: 60_000,
  });
}

// ─── Salt-okunur uzak katalog (Paraşüt'e gerçek istek atar) ──────────────────

/**
 * Paraşüt'te tanımlı ürün/hizmet listesi. `enabled` false iken istek atılmaz
 * — çağıran taraf "Paraşüt'ten seç" açılınca `enabled: true` verir. Bayrak
 * kapalıyken backend 503 `parasut_disabled` döner; bu hook hatayı YUTMAZ,
 * çağıran taraf `isError`/`error` ile "bağlantı kapalı, elle girin" mesajını
 * kendi gösterir.
 */
export function useParasutRemoteProducts(enabled, pageSize = 25, pageNumber = 1) {
  return useQuery({
    queryKey: parasutMappingKeys.remoteProducts(pageSize, pageNumber),
    queryFn: async () => {
      const res = await apiClient.get(
        `/admin/parasut/products/remote?page_size=${pageSize}&page_number=${pageNumber}`,
      );
      return res.data;
    },
    enabled: Boolean(enabled),
    retry: false,
    staleTime: 30_000,
  });
}

/** `useParasutRemoteProducts` ile birebir aynı desen — kasa/banka/POS hesapları. */
export function useParasutRemoteAccounts(enabled, pageSize = 25, pageNumber = 1) {
  return useQuery({
    queryKey: parasutMappingKeys.remoteAccounts(pageSize, pageNumber),
    queryFn: async () => {
      const res = await apiClient.get(
        `/admin/parasut/accounts/remote?page_size=${pageSize}&page_number=${pageNumber}`,
      );
      return res.data;
    },
    enabled: Boolean(enabled),
    retry: false,
    staleTime: 30_000,
  });
}

// ─── Kurs ↔ ürün eşlemesi ─────────────────────────────────────────────────────

export function useCourseMappings(onlyUnmapped = false, limit = 50, skip = 0) {
  return useQuery({
    queryKey: parasutMappingKeys.courseMappings(onlyUnmapped, limit, skip),
    queryFn: async () => {
      const params = new URLSearchParams({
        only_unmapped: onlyUnmapped ? 'true' : 'false',
        limit: String(limit),
        skip: String(skip),
      });
      const res = await apiClient.get(`/admin/parasut/course-mappings?${params}`);
      return res.data;
    },
  });
}

/** Kurs <-> Paraşüt product.id eşlemesini upsert eder — Paraşüt'e istek atmaz. */
export function useSetCourseMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, data }) =>
      apiClient.put(`/admin/parasut/course-mappings/${courseId}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: parasutMappingKeys.all }),
  });
}

export function useDeleteCourseMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId) =>
      apiClient.delete(`/admin/parasut/course-mappings/${courseId}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: parasutMappingKeys.all }),
  });
}

// ─── Banka/kasa ↔ Paraşüt hesabı eşlemesi ────────────────────────────────────

export function useBankMappings() {
  return useQuery({
    queryKey: parasutMappingKeys.bankMappings,
    queryFn: async () => {
      const res = await apiClient.get('/admin/parasut/bank-mappings');
      return res.data;
    },
  });
}

/**
 * Banka/kasa <-> Paraşüt account.id eşlemesini BU ortam için kurar.
 *
 * Backend para birimi uyuşmazlığında (ve `force` verilmediyse) 409
 * `currency_mismatch` döner — bu hook hatayı YUTMAZ/otomatik force RETRY
 * YAPMAZ (canlı veri, sessiz zorlama riskli); çağıran taraf 409'u yakalayıp
 * kullanıcıya açık onay sorduktan SONRA `force: true` ile yeniden çağırır.
 */
export function useSetBankMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankId, data }) =>
      apiClient.put(`/admin/parasut/bank-mappings/${bankId}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: parasutMappingKeys.all }),
  });
}

export function useDeleteBankMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankId) =>
      apiClient.delete(`/admin/parasut/bank-mappings/${bankId}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: parasutMappingKeys.all }),
  });
}

// ─── Outbox izleme (salt-okunur) ─────────────────────────────────────────────

/** Durum başına outbox satır sayısı (pending/in_progress/.../blocked/...). */
export function useOutboxSummary() {
  return useQuery({
    queryKey: parasutMappingKeys.outboxSummary,
    queryFn: async () => {
      const res = await apiClient.get('/admin/parasut/outbox/summary');
      return res.data;
    },
    staleTime: 30_000,
  });
}

/**
 * `blocked` satırların listesi. `blockReason` boş string/undefined ise
 * backend'e HİÇ gönderilmez (tümü listelenir) — `missing_product_mapping` /
 * `missing_tax_number` / `depends_on` ile filtrelenebilir.
 */
export function useOutboxBlocked(blockReason = '', limit = 50, skip = 0) {
  return useQuery({
    queryKey: parasutMappingKeys.outboxBlocked(blockReason, limit, skip),
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
      if (blockReason) params.set('block_reason', blockReason);
      const res = await apiClient.get(`/admin/parasut/outbox/blocked?${params}`);
      return res.data;
    },
  });
}

// ── FAZ 4 — Outbox genel izleme + yönetim (retry / needs_verification / unknown) ──

/**
 * Outbox satırlarının GENEL listesi — `status`/`entityType` filtreli.
 * `status`/`entityType` boş string ise backend'e hiç gönderilmez (tümü).
 * Yanıt satırları `payload` TAŞIMAZ (backend bilerek dışarıda bırakır, TCKN
 * sızıntısı önlemi — bkz. routes/parasut_admin.py `_OUTBOX_LIST_PROJECTION`).
 */
export function useOutboxList(status = '', entityType = '', limit = 50, skip = 0) {
  return useQuery({
    queryKey: parasutMappingKeys.outboxList(status, entityType, limit, skip),
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
      if (status) params.set('status', status);
      if (entityType) params.set('entity_type', entityType);
      const res = await apiClient.get(`/admin/parasut/outbox?${params}`);
      return res.data;
    },
  });
}

/** `failed` bir işi ELLE `pending`'e geri alır. `mutateAsync({ outboxId, note })` — `note` ZORUNLU. */
export function useRetryOutboxItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ outboxId, note }) =>
      apiClient.post(`/admin/parasut/outbox/${outboxId}/retry`, { note }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: parasutMappingKeys.all }),
  });
}

/**
 * `needs_verification` bir kaydı ELLE çözer.
 * `mutateAsync({ outboxId, resolution: 'pending'|'done'|'failed', note, parasutId?, confirmedNotInParasut? })`
 *
 * ⚠️ Kontrat GÜNCELLEMESİ (aynı gün, koordinatör mesajı — backend'de bu turda
 * HENÜZ YAZILIYOR, salt okumada resolve_needs_verification_manual bu iki
 * kuralı henüz UYGULAMIYORDU): `resolution="pending"` seçilince body'de
 * `confirmed_not_in_parasut: true` ZORUNLU olacak (yoksa 400 `parasut_
 * kontrol_onayi_gerekli`) — admin'in Paraşüt panelinde faturanın GERÇEKTEN
 * olmadığını kontrol ettiğini teyit eder (çift fatura riski). `resolution=
 * "done"` için `parasut_id` ZORUNLU olacak. UI (`AdminParasutMapping.jsx`
 * `OutboxListRow`) bu iki alanı FORM SEVİYESİNDE zorunlu kılar; backend
 * teslim edilene kadar `confirmed_not_in_parasut` body'de fazladan bir alan
 * olarak gönderilir (backend henüz okumuyorsa zararsız, eski davranış
 * DEĞİŞMEZ).
 */
export function useResolveNeedsVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ outboxId, resolution, note, parasutId, confirmedNotInParasut }) =>
      apiClient
        .post(`/admin/parasut/outbox/${outboxId}/resolve-needs-verification`, {
          resolution,
          note,
          ...(parasutId ? { parasut_id: parasutId } : {}),
          ...(resolution === 'pending' ? { confirmed_not_in_parasut: !!confirmedNotInParasut } : {}),
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: parasutMappingKeys.all }),
  });
}

/** `unknown` bir kaydı ELLE çözer. `mutateAsync({ outboxId, resolution: 'done'|'failed', note })` */
export function useResolveUnknown() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ outboxId, resolution, note }) =>
      apiClient
        .post(`/admin/parasut/outbox/${outboxId}/resolve-unknown`, { resolution, note })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: parasutMappingKeys.all }),
  });
}

// ── FAZ 4 — tahsilat ↔ fatura durumu (Borç & Tahsilat ledger ekranı) ─────────

/**
 * Bir öğrencinin tahsilat satırları için Paraşüt fatura durumunu TOPLU çeker
 * (satır başına istek YOK — `GET .../payments/invoice-status?payment_ids=a,b,c`).
 *
 * @param {string[]} paymentIds — boşsa istek hiç atılmaz.
 * @returns `{ results: { [payment_id]: {found,outbox_id,status,parasut_id,
 *   invoice_no,link,manual_credit_note_required,reversal_id,refunded_marked,
 *   refunded_note,block_reason} } }` — bkz. routes/parasut_admin.py
 *   `_invoice_status_summary` (salt okundu, bu turda backend'e DOKUNULMADI).
 *   `outbox_id` Tur 3'te eklendi (`parasutStatusCell.jsx` doğrudan retry için
 *   kullanır). `invoice_no`/`link` bu fazda HER ZAMAN `null` (backend henüz
 *   doldurmuyor, Faz C konusu) — UI bu ikisine GÖRÜNÜRDE bel bağlamaz.
 */
export function usePaymentsInvoiceStatusBulk(paymentIds = []) {
  const ids = (paymentIds || []).filter(Boolean);
  return useQuery({
    queryKey: parasutMappingKeys.paymentsInvoiceStatus(ids),
    queryFn: async () => {
      const res = await apiClient.get('/admin/parasut/payments/invoice-status', {
        params: { payment_ids: ids.join(',') },
      });
      return res.data;
    },
    enabled: ids.length > 0,
    staleTime: 15_000,
  });
}

/**
 * Bir tahsilatın Paraşüt faturası için "iade edildi" İŞARETİ koyar — GERÇEK
 * bir Paraşüt işlemi YAPMAZ, yalnızca bizim tarafımızda bir not/bayrak (bkz.
 * backend docstring'i). `mutateAsync({ paymentId, note })` — `note` ZORUNLU.
 */
export function useMarkPaymentRefunded() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, note }) =>
      apiClient.post(`/admin/parasut/payments/${paymentId}/mark-refunded`, { note }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parasutMapping', 'paymentsInvoiceStatus'] }),
  });
}

// ── Genel "Eğitim Ödemesi" ürün eşlemesi (Tur 3'te backend'de HAZIR doğrulandı) ──

/**
 * `GET /admin/parasut/invoice-item-mapping` — backend HAZIR (Tur 3'te
 * routes/parasut_admin.py'den salt okunarak doğrulandı). Eşleme YOKSA
 * yanıt 200 + JSON gövde DOĞRUDAN `null`'dur (`{"mapping": null}` gibi bir
 * sarmalayıcı DEĞİL) — bu yüzden `res.data` doğrudan döndürülür, `data` bu
 * durumda `null` olur ve çağıran taraf (`InvoiceItemMappingCard`)
 * `data?.parasut_product_id` ile güvenle kontrol eder. `retry:false` +
 * `isError` dalı yine de savunma amaçlı KORUNDU (ağ hatası/beklenmeyen 5xx
 * için), ama artık "henüz kurulmadı" anlamına gelmez.
 * @returns `{parasut_product_id, parasut_product_name} | null`
 */
export function useInvoiceItemMapping() {
  return useQuery({
    queryKey: parasutMappingKeys.invoiceItemMapping,
    queryFn: async () => {
      const res = await apiClient.get('/admin/parasut/invoice-item-mapping');
      return res.data;
    },
    retry: false,
  });
}

/**
 * `PUT /admin/parasut/invoice-item-mapping` — backend HAZIR (Tur 3).
 * `mutateAsync({ parasutProductId, parasutProductName })` — yalnızca
 * `parasutProductId` ZORUNLU (backend `parasut_product_id_zorunlu` 400 döner).
 */
export function useSetInvoiceItemMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parasutProductId, parasutProductName }) =>
      apiClient
        .put('/admin/parasut/invoice-item-mapping', {
          parasut_product_id: parasutProductId,
          parasut_product_name: parasutProductName,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: parasutMappingKeys.invoiceItemMapping }),
  });
}
